import { IActionFactory } from "src/store";
import { createActionWithPayload, createReduxOperation, makeConstants } from "src/store/utils";
import { ThunkResult } from "src/shared/types";
import * as api from "src/api/app";
import { FiltersConfigPayload, NotificationPayload } from "./types";
import { deepClone } from "src/shared/utils/deepClone";
import { devLogging } from "src/utils/logger";
import { getServiceName, ServiceName } from "src/utils/getservicename";

// generate actions, constants and reducer
const {
  actions: [filtersConfigRequest, filtersConfigSuccess, filtersConfigFailure, filtersConfigReset],
  reducer: filtersConfigReducer,
} = createReduxOperation("FILTERS_CONFIG");

const {
  actions: [userRoleAccessRequest, userRoleAccessSuccess, userRoleAccessFailure],
  reducer: userRoleAccessReducer,
} = createReduxOperation("USER_ROLE_ACCESS");

const {
  actions: [appConfigRequest, appConfigSuccess, appConfigFailure],
  reducer: appConfigReducer,
} = createReduxOperation("APP_CONFIG");

const {
  actions: [notificationRequest, notificationSuccess, notificationFailure],
  reducer: notificationReducer,
} = createReduxOperation("NOTIFICATION");

export const CHANGE_TAB = "CHANGE_TAB";
export const SET_SHOW_SNACKBAR = "SET_SHOW_SNACKBAR";
export const TIME_TAB_RESET = "TIME_TAB_RESET";
export const ROLE_CALLOUT_SHOWN = "ROLE_CALLOUT_SHOWN";
export const COMING_BACK_FROM_DETAIL_PAGE = "COMING_BACK_FROM_DETAIL_PAGE";

export const appParentConstants = makeConstants([
  CHANGE_TAB,
  SET_SHOW_SNACKBAR,
  ROLE_CALLOUT_SHOWN,
  COMING_BACK_FROM_DETAIL_PAGE,
]);

export const changeTimeTabAppWide = createActionWithPayload<symbol, string>(
  appParentConstants.get(CHANGE_TAB) as symbol
);
devLogging("log app parents", changeTimeTabAppWide);

export const setShowSnackbar: IActionFactory<symbol, boolean> = createActionWithPayload(
  appParentConstants.get(SET_SHOW_SNACKBAR) as symbol
);
export const setIsRoleCalloutShownAction: IActionFactory<symbol, boolean> = createActionWithPayload(
  appParentConstants.get(ROLE_CALLOUT_SHOWN) as symbol
);

// set if user is coming to main page back from detail page
export const setComingBackFromDetailsPage = createActionWithPayload<symbol, boolean>(
  appParentConstants.get(COMING_BACK_FROM_DETAIL_PAGE) as symbol
);

// handle thunk
export function fetchFiltersConfig(payload?: FiltersConfigPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const { metricId, fresh } = payload || {};
    dispatch(filtersConfigRequest());
    const { response, error } = await api.getFiltersConfig({ fresh, metricId });
    if (response) {
      const formData = JSON.parse(response.config.data.meta.queryParams["form_data"]);
      const userRoles = getState().app.userRoles.payload;
      if (!userRoles.hasFullAccess) {
        formData.filters = [{ col: "city", op: "in", val: userRoles.cities }];
      }
      const clonedResponse = deepClone(response);
      clonedResponse.config.data.meta.queryParams["form_data"] = formData;
      dispatch(filtersConfigSuccess(clonedResponse));
    } else {
      dispatch(filtersConfigFailure(error));
    }
  };
}

export function fetchAppConfig(): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    dispatch(appConfigRequest());
    const { response, error } = await api.getAppConfig();
    if (response) {
      dispatch(appConfigSuccess(response));
    } else {
      dispatch(appConfigFailure(error));
    }
  };
}

export function fetchUserRoles(): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    dispatch(userRoleAccessRequest());
    const { response, error } = await api.getUserRoles();

    if (response) {
      const data = {
        hasFullAccess: response.admin as boolean,
        metrics: response.admin ? [] : ((response.metricNames || []) as string[]),
        cities:
          getServiceName() === ServiceName.ANOBIS_IMCOM
            ? (response.cities as string[])
            : response.admin
            ? []
            : (response.cities as string[]),
        pods: response.pods as string[],
        podNames: response.podNames as string[],
        restaurantIds: response.restaurantIds as string[],
        misRole: response.misRole,
      };

      dispatch(userRoleAccessSuccess(data));
    } else {
      dispatch(userRoleAccessFailure(error));
    }
  };
}

export function fetchNotification(): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    dispatch(notificationRequest());
    const { response, error } = await api.getNotification();
    if (response) {
      const { flag, message, alert } = response;
      const data: NotificationPayload = {
        isVisible: flag,
        message,
        alert,
      };
      dispatch(notificationSuccess(data));
    } else {
      dispatch(notificationFailure(error));
    }
  };
}

export { filtersConfigReset, appConfigReducer, userRoleAccessReducer, filtersConfigReducer, notificationReducer };
